import React, { FC, useState, useRef } from 'react'
import axios from 'axios'
import { Toast } from 'primereact/toast'
import { Button } from 'primereact/button'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Chart } from 'primereact/chart'
import { ProgressSpinner } from 'primereact/progressspinner'
import { Dropdown } from 'primereact/dropdown'
import { PageTitle } from '../../../../_designereyes/layout/core'
import { API_URL } from '../../../modules/auth/core/_requests'

// We'll assume your Node route is /api/sales-velocity
// So the final URL is e.g. http://localhost:3000/api/sales-velocity
const ENDPOINT = `${API_URL}/api/sales-velocity`

interface ForecastRow {
  date: string
  predicted_sales: number
  growth_percent_from_last: number
}

interface FullRecord {
  asin: string
  sales_history: { date: string; quantity: number }[]
  exog_history: { date: string; stock_rate: number; buy_box_percent: number }[]
  forecast_steps: number
  forecasts: ForecastRow[]
}

const SalesVelocityForecastWrapper: FC = () => {
  const [allRecords, setAllRecords] = useState<FullRecord[]>([])
  const [selectedAsin, setSelectedAsin] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const toastRef = useRef<Toast>(null)

  // We'll derive forecast data from the currently selected record
  const selectedRecord = allRecords.find((r) => r.asin === selectedAsin)
  const forecastData = selectedRecord ? selectedRecord.forecasts : []

  // Build line chart from forecast data
  const chartData = {
    labels: forecastData.map((row) => row.date),
    datasets: [
      {
        label: 'Predicted Sales',
        data: forecastData.map((row) => row.predicted_sales),
        fill: false,
        borderColor: 'rgba(75,192,192,1)',
        tension: 0.4,
      },
    ],
  }

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { display: true },
    },
    scales: {
      x: {
        ticks: { },
        grid: { },
      },
      y: {
        beginAtZero: true,
        ticks: {  },
        grid: {  },
      },
    },
  }

  const handleFetchAll = async () => {
    try {
      setLoading(true)
      setAllRecords([])

      const resp = await axios.post<{ message: string; data: FullRecord[] }>(ENDPOINT)
      if (resp.status === 200) {
        setAllRecords(resp.data.data)
        toastRef.current?.show({
          severity: 'success',
          summary: 'Records Fetched',
          detail: `Found ${resp.data.data.length} ASINs from local JSON.`,
          life: 4000,
        })
      } else {
        toastRef.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: `Status code: ${resp.status}`,
          life: 5000,
        })
      }
    } catch (error: any) {
      console.error('Error fetching local JSON data:', error)
      toastRef.current?.show({
        severity: 'error',
        summary: 'Fetch Error',
        detail: error.message,
        life: 6000,
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="card text-body bg-body p-4">
      <Toast ref={toastRef} position="bottom-left" />

      <PageTitle>Sales Velocity Forecast (Local JSON, No Python)</PageTitle>

      <div className="d-flex align-items-center mb-3">
        <Button
          label={loading ? 'Loading...' : 'Fetch All Records'}
          icon="pi pi-cloud-download"
          className="btn btn-primary me-3"
          onClick={handleFetchAll}
          disabled={loading}
        />
        {loading && <ProgressSpinner style={{ width: '30px', height: '30px' }} strokeWidth="4" />}

        {allRecords.length > 0 && (
          <Dropdown
            value={selectedAsin}
            onChange={(e) => setSelectedAsin(e.value)}
            options={allRecords.map((r) => ({ label: r.asin, value: r.asin }))}
            placeholder="Select an ASIN"
            className="ms-3"
          />
        )}
      </div>

      {allRecords.length === 0 && !loading && (
        <div className="alert alert-info">
          No data loaded yet. Click "Fetch All Records" to load from local JSON.
        </div>
      )}

      {selectedRecord && (
        <>
          <h4 className="mb-3">
            ASIN: {selectedRecord.asin} (Forecast Steps: {selectedRecord.forecast_steps})
          </h4>
          <div
            style={{
              
              padding: '1rem',
              borderRadius: '8px',
              marginBottom: '2rem',
            }}
          >
            <Chart type="line" data={chartData} options={chartOptions} />
          </div>

          {/* Forecast table */}
          <DataTable
            value={forecastData}
            stripedRows
            paginator
            rows={5}
            className="p-datatable-customers"
            responsiveLayout="scroll"
            style={{ backgroundColor: '#2D2F30' }}
          >
            <Column field="date" header="Date" style={{ minWidth: '120px' }}></Column>
            <Column field="predicted_sales" header="Predicted Sales" style={{ minWidth: '120px' }}></Column>
            <Column
              field="growth_percent_from_last"
              header="Growth % from Last"
              style={{ minWidth: '140px' }}
            ></Column>
          </DataTable>
        </>
      )}
    </div>
  )
}

export default SalesVelocityForecastWrapper
